import {
  addDays,
  isAfter,
  isBefore,
  isSameDay,
  isValid,
  isWithinInterval,
  startOfDay,
} from 'date-fns';
import { useCallback, useEffect, useState } from 'react';

import useDebounce from '../../../../../hooks/useDebounce/useDebounce';
import useSearchParams from '../../../../../hooks/useSearchParams';
import LangEnum from '../../../../../lang/LangEnum';
import { formatDateToApi } from '../../../../../utils/dates';
import { DateShortcut } from '../../../../FilterV3/components/DateRangeFilterV2/dateRangeFilter.types';
import { DATE_SHORTCUT_KEYS } from '../../../../FilterV3/components/DateRangeFilterV2/dateRangeFilter.utils';
import { CommonFilterHookReturn, HandleResetToDefaultValue } from '../commonHookReturn.types';
import { MILLISECONDS_TO_APPLY_FILTER } from '../constants';
import { calculateDate } from './dateRangeFilter.utils';

const ARRAY_VALIDATE_SAME_DAY_SHORTCUT = [
  DateShortcut.SEVEN_DAYS,
  DateShortcut.THIRTY_DAYS,
  DateShortcut.SIXTY_DAYS,
  DateShortcut.NINETY_DAYS,
  DateShortcut.TWELVE_MONTHS,
  DateShortcut.DAY,
];

const ARRAY_VALIDATE_PERIOD_SHORTCUT = [
  DateShortcut.QUARTER,
  DateShortcut.WEEK,
  DateShortcut.YEAR,
  DateShortcut.MONTH,
];

export interface UseDateRangeFilterParams {
  initialDateKey: string;
  finalDateKey: string;
  initialDateLabel?: LangEnum;
  finalDateLabel?: LangEnum;
  finalDateDefaultValue?: string;
  initialDateDefaultValue?: string;
  shouldIgnoreDefaultFilters?: boolean;
  shouldIgnore?: boolean;
  shortcutDefaultValue?: DateShortcut | undefined;
  shortcutStepDefaultValue?: DateShortcut | undefined;
  shortcutKey?: string;
  shortcutStepKey?: string;
}

export interface UseDateRangeFilterOutput extends CommonFilterHookReturn {
  hasSelectedValue: boolean;
  handleUpdateInternalState: (newExpandableState: boolean) => void;
  initialDate: Date | null;
  finalDate: Date | null;
  initialDateLabel: LangEnum;
  finalDateLabel: LangEnum;
  setInitialDate: (newInitialDate: Date | null) => void;
  setFinalDate: (newFinalDate: Date | null) => void;
  initialDateKey: string;
  finalDateKey: string;
  shortcut: DateShortcut | null;
  shortcutStep: number | null;
  setShortcut: (newShortcut: DateShortcut | null) => void;
  setShortcutStep: (newShortcutStep: number | number) => void;
}

const useDateRangeFilter = ({
  finalDateKey,
  initialDateKey,
  initialDateLabel,
  finalDateLabel,
  finalDateDefaultValue,
  initialDateDefaultValue,
  shouldIgnoreDefaultFilters = true,
  shouldIgnore,
  shortcutDefaultValue,
  shortcutStepDefaultValue,
  shortcutKey,
  shortcutStepKey,
}: UseDateRangeFilterParams): UseDateRangeFilterOutput => {
  const { searchParams, setSearchParams } = useSearchParams();

  const formatDefaultValues = useCallback((defaultValues: Array<string | null>) => {
    const formattedDefaultValues = defaultValues.map((defaultValue) => {
      if (!defaultValue || !isValid(new Date(defaultValue))) {
        return null;
      }

      const dateOnly = defaultValue.includes('T') ? defaultValue.split('T')[0] : defaultValue;
      const date = new Date(`${dateOnly}T00:00:00`);
      if (!isValid(date)) {
        return null;
      }
      return date;
    });
    return formattedDefaultValues;
  }, []);

  const getDefaultValues = (): Array<Date | null> => {
    const defaultValuesFromSearch = [searchParams[initialDateKey], searchParams[finalDateKey]];

    const shortcutFromSearch = searchParams[shortcutKey || DATE_SHORTCUT_KEYS.dateShortcutKey];
    const effectiveShortcut = shortcutFromSearch ?? shortcutDefaultValue;

    const hasSearchValueAndIsNotAllPeriod =
      defaultValuesFromSearch.some((defaultValue) => !!defaultValue) &&
      effectiveShortcut !== DateShortcut.ALL_PERIOD;
    const userDefaultValues =
      shouldIgnoreDefaultFilters && hasSearchValueAndIsNotAllPeriod
        ? defaultValuesFromSearch
        : [initialDateDefaultValue ?? null, finalDateDefaultValue ?? null];

    return formatDefaultValues(userDefaultValues);
  };

  const [dateValues, setDateValues] = useState<Array<Date | null>>(getDefaultValues);
  const [shortcut, setShortcut] = useState<DateShortcut | null>(
    searchParams[shortcutKey || DATE_SHORTCUT_KEYS.dateShortcutKey] ?? shortcutDefaultValue,
  );
  const [shortcutStep, setShortcutStep] = useState<number | null>(
    searchParams[shortcutStepKey || DATE_SHORTCUT_KEYS.shortcutStepKey] ?? shortcutStepDefaultValue,
  );

  const debouncedDateValues = useDebounce(dateValues, MILLISECONDS_TO_APPLY_FILTER);
  const debouncedShortcut = useDebounce(shortcut, MILLISECONDS_TO_APPLY_FILTER);
  const debouncedShortcutStep = useDebounce(shortcutStep, MILLISECONDS_TO_APPLY_FILTER);

  const revalidateDates = ({
    initialDate,
    finalDate,
    shortcut,
    shortcutStep,
  }: {
    initialDate: string;
    finalDate: string;
    shortcut: DateShortcut;
    shortcutStep?: number | null;
  }) => {
    const parsedInitialDate = startOfDay(new Date(`${initialDate}T00:00:00`));
    const parsedFinalDate = startOfDay(new Date(`${finalDate}T00:00:00`));
    const step = shortcutStep ?? 0;
    const startOfToday = addDays(startOfDay(new Date()), Number(step));
    const dates = calculateDate(
      {
        initialDate: parsedInitialDate,
        finalDate: parsedFinalDate,
        shortcut,
        shortcutStep: step,
      },
      step,
      shortcut,
    );

    const shouldValidateSameDay =
      ARRAY_VALIDATE_SAME_DAY_SHORTCUT.includes(shortcut) &&
      !isSameDay(startOfToday, parsedFinalDate);

    const shouldValidateRange =
      ARRAY_VALIDATE_PERIOD_SHORTCUT.includes(shortcut) &&
      !isWithinInterval(startOfToday, {
        start: parsedInitialDate,
        end: parsedFinalDate,
      });

    if (shouldValidateSameDay || shouldValidateRange) {
      setDateValues([dates.initialDate ?? null, dates.finalDate ?? null]);
      return {
        initialDate: formatDateToApi(dates.initialDate),
        finalDate: formatDateToApi(dates.finalDate),
      };
    }
  };

  const getDates = () => {
    if (shortcut === DateShortcut.ALL_PERIOD) {
      return {
        initialDate: undefined,
        finalDate: undefined,
      };
    }
    const initialDate = dateValues[0] && formatDateToApi(dateValues[0]);
    const finalDate = dateValues[1] && formatDateToApi(dateValues[1]);

    if (initialDate && finalDate && shortcut) {
      revalidateDates({
        initialDate,
        finalDate,
        shortcut,
        shortcutStep,
      });
    }
    return {
      initialDate,
      finalDate,
    };
  };

  useEffect(() => {
    if (shouldIgnore) {
      return;
    }

    const { finalDate, initialDate } = getDates();
    setSearchParams({
      [initialDateKey]: initialDate || undefined,
      [finalDateKey]: finalDate || undefined,
      [shortcutKey || DATE_SHORTCUT_KEYS.dateShortcutKey]: shortcut || undefined,
      [shortcutStepKey || DATE_SHORTCUT_KEYS.shortcutStepKey]: shortcutStep || undefined,
    });
  }, [debouncedDateValues, shortcut, shortcutStep, debouncedShortcut, debouncedShortcutStep]);

  const [hasSelectedValue, setHasSelectedValue] = useState(getDefaultValues().some((date) => date));

  const handleUpdateInternalState = useCallback(
    (newExpandableState: boolean) => {
      if (newExpandableState === true) {
        return;
      }
      setHasSelectedValue(dateValues.some((date) => date));
    },
    [dateValues],
  );

  const getNewValueBasedOn = useCallback(
    (on: 'initial' | 'final', initialDate: Date | null, finalDate: Date | null) => {
      if (on === 'initial') {
        if (initialDate && finalDate) {
          const newValueIsAfter = isAfter(initialDate, finalDate);
          if (newValueIsAfter) {
            return null;
          }
        }

        return finalDate;
      }

      if (finalDate && initialDate) {
        const newValueIsBefore = isBefore(finalDate, initialDate);
        if (newValueIsBefore) {
          return null;
        }
      }

      return initialDate;
    },
    [],
  );

  const handleSetValues = useCallback(
    (to: 'initial' | 'final', value: Date | null) => {
      setDateValues((oldValues) => {
        if (to === 'initial') {
          const newFinalDate = getNewValueBasedOn('initial', value, oldValues[1]);
          return [value, newFinalDate];
        }
        const newInitialDate = getNewValueBasedOn('final', oldValues[0], value);
        return [newInitialDate, value];
      });
    },
    [getNewValueBasedOn],
  );

  const cleanFilter = useCallback(() => {
    setDateValues([null, null]);
    setShortcut(null);
  }, []);

  const getSearchParamKeys = useCallback(
    () => [
      shortcutKey || DATE_SHORTCUT_KEYS.dateShortcutKey,
      shortcutStepKey || DATE_SHORTCUT_KEYS.shortcutStepKey,
      finalDateKey,
      initialDateKey,
    ],
    [shortcutKey, shortcutStepKey, finalDateKey, initialDateKey],
  );

  const handleResetToDefaultValue: HandleResetToDefaultValue = useCallback(() => {
    if (!finalDateDefaultValue && !initialDateDefaultValue) {
      cleanFilter();
      return;
    }
    const formattedValues = formatDefaultValues([
      initialDateDefaultValue ?? null,
      finalDateDefaultValue ?? null,
    ]);
    setDateValues(formattedValues);
  }, [cleanFilter, finalDateDefaultValue, initialDateDefaultValue, formatDefaultValues]);

  const handleSetFinalDate = useCallback(
    (newFinalDate: Date | null) => handleSetValues('final', newFinalDate),
    [handleSetValues],
  );

  const handleSetInitialDate = useCallback(
    (newInitialDate: Date | null) => handleSetValues('initial', newInitialDate),
    [handleSetValues],
  );

  return {
    hasSelectedValue,
    handleUpdateInternalState,
    initialDate: dateValues[0],
    finalDate: dateValues[1],
    setInitialDate: handleSetInitialDate,
    setFinalDate: handleSetFinalDate,
    setShortcut,
    setShortcutStep,
    shortcut,
    shortcutStep,
    initialDateKey,
    finalDateKey,
    cleanFilter,
    getSearchParamKeys,
    handleResetToDefaultValue,
    initialDateLabel: initialDateLabel ?? LangEnum.GLOBAL_CREATED_AT,
    finalDateLabel: finalDateLabel ?? LangEnum.GLOBAL_FINAL_DATE,
  };
};

export default useDateRangeFilter;
